import React, { useCallback, useEffect, useState } from 'react';
import SkeletonTable from '../components/ui/SkeletonTable.tsx';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  // CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  // Snackbar,
  TextField,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import UpdateIcon from '@mui/icons-material/Update';
import { toastSuccess, toastError } from '../shared/ui/toast.ts';
import dayjs from 'dayjs';
import {
  getWaitlistEntries,
  notifyWaitlistEntry,
  updateWaitlistStatus,
} from '../services/apiService.ts';

const statusColors: Record<string, any> = {
  WAITING: 'warning',
  NOTIFIED: 'info',
  BOOKED: 'success',
  CANCELED: 'default',
  EXPIRED: 'default',
};

const WaitlistPage: React.FC = () => {
  const { tenantId } = useParams();
  // toast via helpers

  const [status, setStatus] = useState<string>('ALL');
  const [partySize, setPartySize] = useState<string>('');
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState<number>(0);

  const fetchData = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const res = await getWaitlistEntries({
        tenantId,
        status: status === 'ALL' ? undefined : status,
        partySize: partySize ? Number(partySize) : undefined,
        startTime: from || undefined,
        endTime: to || undefined,
        page,
        size: pageSize,
        sort: 'createdAt,DESC',
      });
      const content = res?.content || res || [];
      setRows(content.map((e: any) => ({ ...e, id: e.id })));
      setTotal(res?.totalElements ?? content.length);
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || e.message;
      toastError(msg || 'Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  }, [tenantId, status, partySize, from, to, page, pageSize]);

  useEffect(() => {
    fetchData();
    const int = setInterval(fetchData, 10000); // polling 10s
    return () => clearInterval(int);
  }, [fetchData]);

  const handleNotify = async (id: number) => {
    try {
      await notifyWaitlistEntry(id);
      toastSuccess('Notification sent');
      fetchData();
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || e.message;
      toastError(msg || 'Failed to notify');
    }
  };

  const handleUpdateStatus = async (id: number, newStatus: string) => {
    try {
      await updateWaitlistStatus(id, newStatus);
      toastSuccess('Status updated');
      fetchData();
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || e.message;
      toastError(msg || 'Failed to update status');
    }
  };

  const cols: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'name', headerName: 'Name', width: 160 },
    { field: 'phone', headerName: 'Phone', width: 140 },
    { field: 'partySize', headerName: 'Party', width: 90 },
    {
      field: 'desiredTime', headerName: 'Desired Time', width: 170,
      valueGetter: (p) => p.row.desiredTime ? dayjs(p.row.desiredTime).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      field: 'status', headerName: 'Status', width: 140,
      renderCell: (p: GridRenderCellParams) => (
        <Chip size="small" label={p.value} color={statusColors[p.value as string] || 'default'} />
      )
    },
    {
      field: 'waitTime', headerName: 'Waited', width: 110,
    },
    {
      field: 'actions', headerName: 'Actions', width: 260,
      renderCell: (p: GridRenderCellParams) => (
        <Box display="flex" gap={1}>
          <Tooltip title="Notify now">
            <span>
              <Button
                size="small"
                variant="contained"
                startIcon={<NotificationsActiveIcon />}
                onClick={() => handleNotify(p.row.id)}
                disabled={p.row.status !== 'WAITING'}
              >
                Notify
              </Button>
            </span>
          </Tooltip>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Update Status</InputLabel>
            <Select
              label="Update Status"
              value={p.row.status}
              onChange={(e: SelectChangeEvent) => handleUpdateStatus(p.row.id, e.target.value)}
            >
              {['WAITING','NOTIFIED','BOOKED','CANCELED','EXPIRED'].map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )
    }
  ];

  return (
    <Box p={2}>
      <Box display="flex" gap={2} alignItems="center" mb={2}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select value={status} label="Status" onChange={(e) => { setStatus(e.target.value); setPage(0); }}>
            {['ALL','WAITING','NOTIFIED','BOOKED','CANCELED','EXPIRED'].map(s => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField size="small" label="Party Size" value={partySize} onChange={(e)=>setPartySize(e.target.value)} sx={{ width: 120 }} />
        <TextField size="small" label="From (ISO)" placeholder="YYYY-MM-DDTHH:mm" value={from} onChange={(e)=>setFrom(e.target.value)} sx={{ width: 200 }} />
        <TextField size="small" label="To (ISO)" placeholder="YYYY-MM-DDTHH:mm" value={to} onChange={(e)=>setTo(e.target.value)} sx={{ width: 200 }} />
        <Button variant="outlined" startIcon={<UpdateIcon/>} onClick={fetchData}>Refresh</Button>
      </Box>

      {loading ? <SkeletonTable /> : (
        <DataGrid
          rows={rows}
          columns={cols}
          autoHeight
          paginationMode="server"
          rowCount={total}
          pageSizeOptions={[10,20,50,100]}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(m)=>{ setPage(m.page); setPageSize(m.pageSize); }}
        />
      )}
    </Box>
  );
};

export default WaitlistPage;
